//FCRA
import React, { useEffect, useState } from "react";
import { getOrderByID } from "../../../services/orderService";
import { Order } from "../../../interfaces/order";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getOrderFormByID,
  saveBackgroundCheckAgreementForm,
} from "../../../services/orderFormService";
import { OrderForm } from "../../../interfaces/orderForm";
import { Card, ListGroup, Table } from "react-bootstrap";
import { getSimpleClient } from "../../../services/clientService";
import { SimpleClient } from "../../../interfaces/client";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import { isValidDate } from "../../../services/helpers";

const BackgroundCheckAgreementForm = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const location = useLocation();
  const { orderID, orderFormID } = location.state || {};
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrderByID(orderID);
        setOrder(order);
        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);

        const client = await getSimpleClient(order.clientID);
        setClient(client);
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [orderID, orderFormID]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (field in authorization) {
      setAuthorization((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    }
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoadingSpinner(true);

    try {
      if ((await validateFields()) && order && orderForm) {
        const clientResponse = await getSimpleClient(order.clientID);
        const result = await saveBackgroundCheckAgreementForm(
          order,
          orderForm?.orderFormID,
          clientResponse.diocese ? clientResponse.diocese : "",
          authorization,
        );

        if (result) {
          navigate("/candidate/needs-review", {
            state: { successMessage: "Form successfully submitted." },
          });
        }
      }
    } catch (error) {
      console.error("Error saving criminal records authorization:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>
      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-form-header">BACKGROUND CHECK AGREEMENT</div>
          <div className="text-form-header">{client?.diocese}</div>
          <br></br>
          <div className="text-container-scroll">
            <div className="text-body">
              <b>
                BACKGROUND INVESTIGATIVE REPORTS DISCLOSURE & AUTHORIZATION
                REGARDING PROCUREMENT OF AN INVESTIGATIVE CONSUMER REPORT
              </b>
            </div>
            <br></br>
            <div>
              In connection with your application for employment or when
              deciding whether to modify or continue your employment (if you are
              hired), we may obtain and use an "investigative consumer report"
              about you from a "consumer reporting agency." A "consumer
              reporting agency" is a person or business which, for monetary
              fees, dues, or on a cooperative nonprofit basis, regularly
              assembles or evaluates consumer credit information or other
              information on consumers for the purpose of furnishing
              "investigative consumer reports" to third parties.
            </div>
            <br></br>
            <div>
              This report may include information as to your character, general
              reputation, personal characteristics and mode of living. This
              information may be obtained by contacting your previous employers
              and/or references supplied by you or others.
            </div>
            <br></br>
            <div>
              In the event that information from the report is utilized in whole
              or in part in making an adverse decision with regard to your
              employment. before making the adverse decision, we will provide
              you with a copy of the investigative consumer report and a
              description in writing of your rights under the law.
            </div>
            <br></br>
            <div>
              Please be advised that you have the right to request, in writing,
              within a reasonable time, that we make a complete and accurate
              disclosure of the nature and scope of the information requested.
              Such disclosure will be made to you within 5 days of the date on
              which we receive the request from you or within 5 days of the time
              the report was first requested.
            </div>
            <br></br>
            <div className="text-body">
              <b>
                The Fair Credit Reporting Act gives you specific rights in
                dealing with consumer reporting agencies:
              </b>
            </div>
            <br></br>
            <p>
              The federal Fair Credit Reporting Act (FCRA) promotes the
              accuracy, fairness, and privacy of information in the files of
              consumer reporting agencies. There are many types of consumer
              reporting agencies, including credit bureaus and specialty
              agencies (such as agencies that sell information about check
              writing histories, medical records, and rental history records).
              Here is a summary of your major rights under the FCRA.
            </p>
            <p>
              <strong>
                For more information, including information about additional
                rights, go to{" "}
                <a
                  href="http://www.consumerfinance.gov/learnmore"
                  style={{ color: "#0000FF" }}
                >
                  www.consumerfinance.gov/learnmore
                </a>{" "}
                or write to: Consumer Financial Protection Bureau, 1700 G Street
                N.W., Washington, DC 20552.
              </strong>
            </p>
            <div className="text-body">Your Rights Under the FCRA</div>
            <Card>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>
                    1. You must be told if information in your file has been
                    used against you.
                  </strong>
                  Anyone who uses a credit report or another type of consumer
                  report to deny your application for credit, insurance, or
                  employment must tell you, and must give you the name, address,
                  and phone number of the agency that provided the information.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    2. You have the right to know what is in your file.
                  </strong>
                  You may request and obtain all the information about you in
                  the files of a consumer reporting agency (your “file
                  disclosure”). You will be required to provide proper
                  identification, which may include your Social Security number.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    3. You have the right to ask for a credit score.
                  </strong>
                  Credit scores are numerical summaries of your
                  credit-worthiness based on information from credit bureaus.
                  You may request a credit score from consumer reporting
                  agencies that create scores or distribute scores used in
                  residential real property loans, but you will have to pay for
                  it.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    4. You have the right to dispute incomplete or inaccurate
                    information.
                  </strong>
                  If you identify information in your file that is incomplete or
                  inaccurate, and report it to the consumer reporting agency,
                  the agency must investigate unless your dispute is frivolous.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    5. Consumer reporting agencies must correct or delete
                    inaccurate, incomplete, or unverifiable information.
                  </strong>
                  Inaccurate, incomplete or unverifiable information must be
                  removed or corrected, usually within 30 days. However, a
                  consumer reporting agency may continue to report information
                  it has verified as accurate.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    6. Consumer reporting agencies may not report outdated
                    negative information.
                  </strong>
                  In most cases, a consumer reporting agency may not report
                  negative information that is more than seven years old, or
                  bankruptcies that are more than 10 years old.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>7. Access to your file is limited.</strong>A consumer
                  reporting agency may provide information about you only to
                  people with a valid need — usually to consider an application
                  with a creditor, insurer, employer, landlord, or other
                  business.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    8. You must give your consent for reports to be provided to
                    employers.
                  </strong>
                  A consumer reporting agency may not give out information about
                  you to your employer, or a potential employer, without your
                  written consent.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    9. You may limit "prescreened" offers of credit and
                    insurance you get based on information in your credit
                    report.
                  </strong>
                  Unsolicited "prescreened" offers for credit and insurance must
                  include a toll-free phone number you can call if you choose to
                  remove your name and address from the lists these offers are
                  based on.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>10. You may seek damages from violators.</strong>
                  If a consumer reporting agency, or, in some cases, a user of
                  consumer reports or a furnisher of information to a consumer
                  reporting agency violates the FCRA, you may be able to sue in
                  state or federal court.
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>
                    11. Identity theft victims and active duty military
                    personnel have additional rights.
                  </strong>
                  For more information, visit{" "}
                  <a
                    href="http://www.consumerfinance.gov/learnmore"
                    style={{ color: "#0000FF" }}
                  >
                    www.consumerfinance.gov/learnmore
                  </a>
                  .
                </ListGroup.Item>
              </ListGroup>
            </Card>
            <br></br>
            <p>
              <strong>
                States may enforce the FCRA, and many states have their own
                consumer reporting laws. In some cases, you may have more rights
                under state law. For more information, contact your state or
                local consumer protection agency or your state Attorney General.
              </strong>
            </p>

            <Table bordered responsive>
              <thead>
                <tr>
                  <th>Type of Business</th>
                  <th>Contact</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    1. Banks, savings associations, and credit unions with total
                    assets of over $10 billion and their affiliates
                  </td>
                  <td>
                    <strong>Consumer Financial Protection Bureau</strong>
                    <br />
                    1700 G Street NW
                    <br />
                    Washington, DC 20552
                    <br />
                    <strong>Federal Trade Commission:</strong>
                    <br />
                    Consumer Response Center – FCRA
                    <br />
                    Washington, DC 20580
                    <br />
                    (877) 382-4357
                  </td>
                </tr>
                <tr>
                  <td>
                    2. National banks, federal savings associations and federal
                    branches and federal agencies of foreign banks
                  </td>
                  <td>
                    <strong>Office of the Comptroller of the Currency</strong>
                    <br />
                    Customer Assistance Group
                    <br />
                    1301 McKinney Street, Suite 3450
                    <br />
                    Houston, TX 77010-9050
                    <br />
                    <strong>Federal Reserve Consumer Help Center</strong>
                    <br />
                    PO Box 1200
                    <br />
                    Minneapolis, MN 55480
                    <br />
                    <strong>FDIC Consumer Response Center</strong>
                    <br />
                    1100 Walnut St., Box #11
                    <br />
                    Kansas City, MO 64106
                  </td>
                </tr>
                <tr>
                  <td>3. Air carriers</td>
                  <td>
                    Asst. General Counsel for Aviation Enforcement &amp;
                    Proceedings
                    <br />
                    Aviation Consumer Protection Division
                    <br />
                    Department of Transportation
                    <br />
                    1200 New Jersey Avenue, S.E.
                    <br />
                    Washington, DC 20590
                  </td>
                </tr>
                <tr>
                  <td>4. Creditors Subject to Surface Transportation Board</td>
                  <td>
                    Office of Proceedings, Surface Transportation Board
                    <br />
                    Department of Transportation
                    <br />
                    395 E Street, S.W.
                    <br />
                    Washington, DC 20423
                  </td>
                </tr>
                <tr>
                  <td>
                    5. Creditors Subject to Packers and Stockyards Act, 1921
                  </td>
                  <td>
                    Nearest Packers and Stockyards Administration area
                    Supervisor
                  </td>
                </tr>
                <tr>
                  <td>6. Small Business Investment Companies</td>
                  <td>
                    Associate Deputy Administrator for Capital Access
                    <br />
                    United States Small Business Administration
                    <br />
                    409 Third Street, SW, 8th Floor
                    <br />
                    Washington, DC 20416
                  </td>
                </tr>
                <tr>
                  <td>7. Brokers and Dealers</td>
                  <td>
                    Securities and Exchange Commission
                    <br />
                    100 F Street, N.E. Washington, DC 20549
                  </td>
                </tr>
                <tr>
                  <td>
                    8. Federal Land Banks, Federal Land Bank Associations,
                    Federal Intermediate Credit Banks and Production Credit
                    Associations
                  </td>
                  <td>
                    Farm Credit Administration
                    <br />
                    1501 Farm Credit Drive
                    <br />
                    McLean, VA 22102-5090
                  </td>
                </tr>
                <tr>
                  <td>
                    9. Retailers, Finance Companies, and All Other Creditors Not
                    Listed Above
                  </td>
                  <td>
                    FTC Regional Office for region in which the creditor
                    operates or Federal Trade Commission:
                    <br />
                    Consumer Response Center - FCRA
                    <br />
                    Washington, DC 20580 (877) 382-4357
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
          <br></br>

          <AuthorizationForm
            authText="Yes, I have read and understand the FCRA Disclosure/Authorization and my rights under the Fair Credit Reporting Act. By checking this box, I am providing my electronic signature to this authorization form and agree that such signature is the legally-binding equivalent to my handwritten signature."
            authorization={authorization}
            validationErrors={validationErrors}
            onInputChange={handleInputChange}
            type={""}
          />
          <br></br>
          <div className="btn-center">
            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Submit
            </button>
          </div>
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default BackgroundCheckAgreementForm;
